import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';

export default {
	name: 'HtmlEditor',

	components: {
		EditorContent,
	},

	props: {
		value: {
			type: String,
			default: '',
		},
		label: {
			type: String,
			default: null,
		},
		placeholder: {
			type: String,
			default: 'Escriba aquí...',
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		error: {
			type: String,
			default: null,
		},
	},

	data() {
		return {
			editor: null,
			isFocused: false,
		};
	},

	computed: {
		hasContent() {
			return this.value && this.value.length > 0;
		},
	},

	watch: {
		value(newValue) {
			if (this.editor && this.editor.getHTML() !== newValue) {
				this.editor.commands.setContent(newValue || '', false);
			}
		},
		readonly(val) {
			if (this.editor) {
				this.editor.setEditable(!val);
			}
		},
	},

	mounted() {
		this.editor = new Editor({
			content: this.value || '',
			editable: !this.readonly,
			extensions: [
				StarterKit.configure({
					heading: { levels: [1, 2, 3] },
				}),
				Table.configure({
					resizable: true,
					HTMLAttributes: {
						class: 'html-editor-table',
					},
				}),
				TableRow,
				TableCell,
				TableHeader,
				Image.configure({
					inline: true,
					allowBase64: true,
				}),
				Underline,
				TextAlign.configure({
					types: ['heading', 'paragraph'],
				}),
			],
			onUpdate: ({ editor }) => {
				const html = editor.getHTML();
				this.$emit('input', html);
			},
			onFocus: () => {
				this.isFocused = true;
			},
			onBlur: () => {
				this.isFocused = false;
			},
		});

		// Handle paste with images
		this.editor.view.dom.addEventListener('paste', this.handlePaste);
	},

	methods: {
		insertTable() {
			this.editor
				.chain()
				.focus()
				.insertTable({ rows: 3, cols: 3, withHeaderRow: true })
				.run();
		},

		addImage() {
			this.$refs.imageInput.click();
		},

		onImageSelected(event) {
			const file = event.target.files[0];
			if (!file) return;

			if (!file.type.startsWith('image/')) return;

			const reader = new FileReader();
			reader.onload = (e) => {
				this.editor
					.chain()
					.focus()
					.setImage({ src: e.target.result })
					.run();
			};
			reader.readAsDataURL(file);

			// Reset input
			event.target.value = '';
		},

		handlePaste(event) {
			const items = event.clipboardData?.items;
			if (!items) return;

			const hasHtml = Array.from(items).some((item) => item.type === 'text/html');
			if (hasHtml) return;

			for (const item of items) {
				if (item.type.startsWith('image/')) {
					event.preventDefault();
					const file = item.getAsFile();
					if (!file) continue;

					const reader = new FileReader();
					reader.onload = (e) => {
						this.editor
							.chain()
							.focus()
							.setImage({ src: e.target.result })
							.run();
					};
					reader.readAsDataURL(file);
					return;
				}
			}
		},
	},

	beforeDestroy() {
		if (this.editor) {
			this.editor.view.dom.removeEventListener('paste', this.handlePaste);
			this.editor.destroy();
		}
	},
};
